/**
 * Versus Mode split-screen renderer.
 *
 * Desktop layout (780Ã—600):
 *   Left panel  (0..359)     â€” local player (full controls, drawn by normal drawBoard)
 *   Divider     (360..419)   â€” VS label, opponent name, trap buttons
 *   Right panel (420..779)   â€” opponent board (display-only, simplified)
 *
 * This module exposes helper functions called by CanvasRenderer.drawVersusMatch().
 */

import {
  VERSUS_CANVAS_HEIGHT,
  VERSUS_PANEL_WIDTH,
  VERSUS_DIVIDER_WIDTH,
  VERSUS_DIVIDER_X,
  VERSUS_OPPONENT_X
} from "../versus/versusConstants";
import { BOARD_SIZE } from "../game/constants";
import type { BoardState, TileData } from "../game/types";
import type { OpponentStats } from "../versus/types";
import { GAME_TILE_COLORS } from "./themePalette";
import {
  resolveGrooveActiveOutline,
  grooveFilledSegmentCount,
  grooveShimmerStrengthForFill,
  grooveShimmerSpeedForFill
} from "./renderConstants";

const GAME_FONT = "'Press Start 2P', monospace";

// Opponent board is drawn at a smaller scale inside the right panel
const OPP_BOARD_PADDING = 10;
const OPP_BOARD_SIZE_PX = VERSUS_PANEL_WIDTH - OPP_BOARD_PADDING * 2; // 340px
const OPP_TILE_SIZE = Math.floor(OPP_BOARD_SIZE_PX / BOARD_SIZE);     // 34px
const OPP_BOARD_ORIGIN_X = VERSUS_OPPONENT_X + Math.floor((VERSUS_PANEL_WIDTH - OPP_TILE_SIZE * BOARD_SIZE) / 2);
const OPP_BOARD_ORIGIN_Y = 130; // Same vertical origin as local board

const TILE_COLORS = GAME_TILE_COLORS;

// â”€â”€ Divider â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function drawDivider(
  ctx: CanvasRenderingContext2D,
  opponentName: string,
  connected: boolean,
  disconnectCountdownSec: number | null
): void {
  const x = VERSUS_DIVIDER_X;
  const w = VERSUS_DIVIDER_WIDTH;

  // Divider background
  ctx.fillStyle = "rgba(10, 15, 30, 0.95)";
  ctx.fillRect(x, 0, w, VERSUS_CANVAS_HEIGHT);

  // Left/right border lines
  ctx.strokeStyle = "#2a4a7a";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 0.5, 0);
  ctx.lineTo(x + 0.5, VERSUS_CANVAS_HEIGHT);
  ctx.moveTo(x + w - 0.5, 0);
  ctx.lineTo(x + w - 0.5, VERSUS_CANVAS_HEIGHT);
  ctx.stroke();

  // VS label
  ctx.save();
  ctx.font = `bold 16px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ff6b35";
  ctx.shadowColor = "#ff6b35";
  ctx.shadowBlur = 8;
  ctx.fillText("VS", x + w / 2, 40);
  ctx.shadowBlur = 0;
  ctx.restore();

  // Opponent name
  ctx.font = `bold 6px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#9fc4ff";
  const truncName = opponentName.length > 8 ? opponentName.slice(0, 8) : opponentName;
  ctx.fillText(truncName, x + w / 2, 65);

  // Connection status
  if (!connected) {
    ctx.fillStyle = "#ff4444";
    ctx.font = `bold 5px ${GAME_FONT}`;
    ctx.fillText("DISC", x + w / 2, 80);
    if (disconnectCountdownSec != null) {
      ctx.fillText(`${Math.ceil(disconnectCountdownSec)}s`, x + w / 2, 90);
    }
  }
}

// â”€â”€ Disconnect overlay (drawn over opponent panel) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function drawDisconnectOverlay(
  ctx: CanvasRenderingContext2D,
  countdownSec: number
): void {
  const boardW = OPP_TILE_SIZE * BOARD_SIZE;
  const boardH = OPP_TILE_SIZE * BOARD_SIZE;

  // Semi-transparent overlay over the opponent board
  ctx.fillStyle = "rgba(15, 10, 10, 0.8)";
  ctx.fillRect(OPP_BOARD_ORIGIN_X, OPP_BOARD_ORIGIN_Y, boardW, boardH);

  const cx = OPP_BOARD_ORIGIN_X + boardW / 2;
  const cy = OPP_BOARD_ORIGIN_Y + boardH / 2;

  // Pulsing red border
  ctx.save();
  const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 300);
  ctx.strokeStyle = `rgba(255, 68, 68, ${(0.4 + pulse * 0.6).toFixed(3)})`;
  ctx.lineWidth = 3;
  ctx.strokeRect(
    OPP_BOARD_ORIGIN_X + 2,
    OPP_BOARD_ORIGIN_Y + 2,
    boardW - 4,
    boardH - 4
  );
  ctx.restore();

  // "DISCONNECTED" text
  ctx.save();
  ctx.font = `bold 8px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ff4444";
  ctx.shadowColor = "#ff4444";
  ctx.shadowBlur = 8;
  ctx.fillText("OPPONENT", cx, cy - 22);
  ctx.fillText("DISCONNECTED", cx, cy - 8);
  ctx.shadowBlur = 0;
  ctx.restore();

  // Countdown
  ctx.font = `bold 14px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffaa44";
  ctx.fillText(`${Math.ceil(countdownSec)}`, cx, cy + 16);

  // Subtitle
  ctx.font = `5px ${GAME_FONT}`;
  ctx.fillStyle = "#888";
  ctx.fillText("FORFEIT IN", cx, cy + 32);
}

// â”€â”€ Opponent board (simplified, display-only) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function drawOpponentBoard(
  ctx: CanvasRenderingContext2D,
  board: BoardState | null,
  fogActive?: boolean
): void {
  // Board background
  ctx.fillStyle = "rgba(8, 12, 24, 0.9)";
  ctx.fillRect(
    OPP_BOARD_ORIGIN_X - 2,
    OPP_BOARD_ORIGIN_Y - 2,
    OPP_TILE_SIZE * BOARD_SIZE + 4,
    OPP_TILE_SIZE * BOARD_SIZE + 4
  );

  // Grid border
  ctx.strokeStyle = "#1a2a4a";
  ctx.lineWidth = 1;
  ctx.strokeRect(
    OPP_BOARD_ORIGIN_X - 2,
    OPP_BOARD_ORIGIN_Y - 2,
    OPP_TILE_SIZE * BOARD_SIZE + 4,
    OPP_TILE_SIZE * BOARD_SIZE + 4
  );

  if (board == null) {
    ctx.font = `bold 7px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.fillStyle = "#4a6a9a";
    ctx.fillText(
      "WAITING...",
      OPP_BOARD_ORIGIN_X + (OPP_TILE_SIZE * BOARD_SIZE) / 2,
      OPP_BOARD_ORIGIN_Y + (OPP_TILE_SIZE * BOARD_SIZE) / 2
    );
    return;
  }

  // Draw tiles (simplified â€” no animations, no drag, no specials glow)
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const cell = board.cells[row]?.[col];
      if (cell == null || cell.tile == null) continue;

      const tile = cell.tile;
      const px = OPP_BOARD_ORIGIN_X + col * OPP_TILE_SIZE + 1;
      const py = OPP_BOARD_ORIGIN_Y + row * OPP_TILE_SIZE + 1;
      const size = OPP_TILE_SIZE - 2;

      drawSimpleTile(ctx, tile, px, py, size);
    }
  }

  // Fog overlay
  if (fogActive) {
    ctx.fillStyle = "rgba(20, 30, 50, 0.85)";
    ctx.fillRect(
      OPP_BOARD_ORIGIN_X,
      OPP_BOARD_ORIGIN_Y,
      OPP_TILE_SIZE * BOARD_SIZE,
      OPP_TILE_SIZE * BOARD_SIZE
    );
    ctx.font = `bold 8px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.fillStyle = "#ff8844";
    ctx.fillText(
      "FOG",
      OPP_BOARD_ORIGIN_X + (OPP_TILE_SIZE * BOARD_SIZE) / 2,
      OPP_BOARD_ORIGIN_Y + (OPP_TILE_SIZE * BOARD_SIZE) / 2
    );
  }
}

function drawSimpleTile(
  ctx: CanvasRenderingContext2D,
  tile: TileData,
  x: number,
  y: number,
  size: number
): void {
  const color = TILE_COLORS[tile.color % TILE_COLORS.length];
  ctx.fillStyle = color;
  ctx.fillRect(x, y, size, size);

  // Subtle highlight
  ctx.fillStyle = "rgba(255,255,255,0.2)";
  ctx.fillRect(x + 1, y + 1, size - 2, 2);

  // Dark border for definition
  ctx.strokeStyle = "rgba(0,0,0,0.3)";
  ctx.lineWidth = 0.5;
  ctx.strokeRect(x, y, size, size);

  // Special indicator
  if (tile.special !== "NONE") {
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    const dotSize = Math.max(4, size * 0.25);
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, dotSize / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Lock indicator
  if (tile.lockHp > 0) {
    ctx.strokeStyle = "#888";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x + 1, y + 1, size - 2, size - 2);
  }
}

// â”€â”€ Opponent HUD (stats bar above board) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function drawOpponentHud(
  ctx: CanvasRenderingContext2D,
  stats: OpponentStats
): void {
  const panelX = VERSUS_OPPONENT_X;
  const boardW = OPP_TILE_SIZE * BOARD_SIZE;
  const headerCenterX = panelX + VERSUS_PANEL_WIDTH / 2;

  ctx.font = `bold 7px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#9fc4ff";
  ctx.fillText(stats.displayName, headerCenterX, OPP_BOARD_ORIGIN_Y - 24);

  ctx.font = `bold 6px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.fillStyle = "#ffd700";
  ctx.fillText(`LV ${stats.level}`, panelX + 28, OPP_BOARD_ORIGIN_Y - 12);
  ctx.fillStyle = "#e5efff";
  ctx.fillText(`SCORE ${stats.score}`, panelX + 82, OPP_BOARD_ORIGIN_Y - 12);

  ctx.textAlign = "right";
  ctx.fillStyle = "#ffd700";
  ctx.fillText(`$${stats.cash}`, panelX + VERSUS_PANEL_WIDTH - 10, OPP_BOARD_ORIGIN_Y - 12);

  // Level progress strip
  const progressX = panelX + 28;
  const progressY = OPP_BOARD_ORIGIN_Y - 2;
  const progressW = VERSUS_PANEL_WIDTH - 36;
  const progressH = 8;
  ctx.fillStyle = "rgba(22,32,52,0.9)";
  ctx.fillRect(progressX, progressY, progressW, progressH);
  ctx.strokeStyle = "#4a6a9f";
  ctx.strokeRect(progressX, progressY, progressW, progressH);
  const levelProgress = Math.max(0, Math.min(1, stats.levelProgressPct));
  ctx.fillStyle = "#7ce6a5";
  ctx.fillRect(progressX + 1, progressY + 1, Math.max(0, (progressW - 2) * levelProgress), progressH - 2);

  // Rotation status + rotation key count (mirrors local control state)
  const rotY = OPP_BOARD_ORIGIN_Y + 12;
  const rotW = 36;
  const rotH = 14;
  const leftOn = stats.pendingRotation === "LEFT";
  const rightOn = stats.pendingRotation === "RIGHT";
  ctx.fillStyle = leftOn ? "#64d8ff" : "rgba(22,32,52,0.92)";
  ctx.fillRect(panelX + 28, rotY, rotW, rotH);
  ctx.strokeStyle = leftOn ? "#b4f3ff" : "#47618f";
  ctx.strokeRect(panelX + 28, rotY, rotW, rotH);
  ctx.fillStyle = leftOn ? "#0b1a2a" : "#8fb6ee";
  ctx.textAlign = "center";
  ctx.font = `bold 5px ${GAME_FONT}`;
  ctx.fillText("LEFT", panelX + 28 + rotW / 2, rotY + rotH / 2 + 1);

  ctx.fillStyle = rightOn ? "#64d8ff" : "rgba(22,32,52,0.92)";
  ctx.fillRect(panelX + 68, rotY, rotW, rotH);
  ctx.strokeStyle = rightOn ? "#b4f3ff" : "#47618f";
  ctx.strokeRect(panelX + 68, rotY, rotW, rotH);
  ctx.fillStyle = rightOn ? "#0b1a2a" : "#8fb6ee";
  ctx.fillText("RIGHT", panelX + 68 + rotW / 2, rotY + rotH / 2 + 1);

  ctx.textAlign = "left";
  ctx.fillStyle = "#8fc1ff";
  ctx.fillText(`KEY ${Math.max(0, Math.floor(stats.rotationKeys))}`, panelX + 112, rotY + rotH / 2 + 1);

  // Vertical doom column, matching local orientation beside the board.
  const doomX = panelX + 6;
  const doomY = OPP_BOARD_ORIGIN_Y;
  const doomW = 16;
  const doomH = 220;
  ctx.fillStyle = "rgba(20, 28, 50, 0.95)";
  ctx.fillRect(doomX, doomY, doomW, doomH);
  ctx.strokeStyle = "rgba(80,100,140,0.8)";
  ctx.strokeRect(doomX, doomY, doomW, doomH);

  const doomFill = 1 - Math.min(1, Math.max(0, stats.doomPct));
  const filledH = doomH * doomFill;
  const doomColor = doomFill > 0.7 ? "#44ff88" : doomFill > 0.35 ? "#ffd700" : "#ff4444";
  ctx.fillStyle = doomColor;
  ctx.fillRect(doomX + 2, doomY + doomH - filledH + 2, doomW - 4, Math.max(0, filledH - 4));

  ctx.font = `bold 4px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.fillStyle = "#9fc4ff";
  ctx.fillText("DOOM", doomX + doomW / 2, doomY + doomH + 8);

  // Groove meter under opponent board (segmented bar matching mobile style).
  const grooveFill = Math.min(1, Math.max(0, stats.groovePct));
  const grooveX = OPP_BOARD_ORIGIN_X;
  const grooveY = OPP_BOARD_ORIGIN_Y + boardW + 8;
  const grooveW = boardW;
  const grooveH = 14;
  const songTimeSec = performance.now() / 1000;

  // Background
  ctx.fillStyle = "rgba(14,24,42,0.92)";
  ctx.fillRect(grooveX, grooveY, grooveW, grooveH);

  // Outline â€” active gets rainbow, otherwise plain
  if (stats.grooveActive) {
    const outline = resolveGrooveActiveOutline(songTimeSec, 120, grooveFill);
    const grad = ctx.createLinearGradient(grooveX, grooveY, grooveX + grooveW, grooveY + grooveH);
    grad.addColorStop(0, `hsla(${outline.hueA.toFixed(1)},100%,62%,${outline.alpha.toFixed(3)})`);
    grad.addColorStop(0.5, `hsla(${outline.hueB.toFixed(1)},100%,60%,${outline.alpha.toFixed(3)})`);
    grad.addColorStop(1, `hsla(${outline.hueC.toFixed(1)},100%,62%,${(outline.alpha * 0.95).toFixed(3)})`);
    ctx.save();
    ctx.shadowColor = `hsla(${outline.hueA.toFixed(1)},100%,58%,${outline.shadowAlpha.toFixed(3)})`;
    ctx.shadowBlur = outline.shadowBlur;
    ctx.strokeStyle = grad;
    ctx.lineWidth = outline.lineWidth;
    ctx.strokeRect(grooveX, grooveY, grooveW, grooveH);
    ctx.restore();
  } else {
    ctx.strokeStyle = "#4a6a9f";
    ctx.lineWidth = 1;
    ctx.strokeRect(grooveX, grooveY, grooveW, grooveH);
  }

  // Segmented bars
  const barsInnerW = grooveW - 6;
  const barsGap = 1;
  const barsCount = 20;
  const barW = Math.max(2, Math.floor((barsInnerW - (barsCount - 1) * barsGap) / barsCount));
  const barsTotalW = barsCount * (barW + barsGap) - barsGap;
  const barsStartX = grooveX + (grooveW - barsTotalW) / 2;
  const barsTopY = grooveY + 2;
  const barsMaxH = grooveH - 4;
  const filledSegments = grooveFilledSegmentCount(grooveFill, barsCount);

  // Empty segment slots
  ctx.save();
  ctx.beginPath();
  ctx.rect(grooveX + 1, grooveY + 1, grooveW - 2, grooveH - 2);
  ctx.clip();

  for (let i = 0; i < barsCount; i++) {
    const bx = barsStartX + i * (barW + barsGap);
    ctx.fillStyle = i < filledSegments ? "rgba(46,66,98,0.72)" : "rgba(20,30,48,0.78)";
    ctx.fillRect(bx, barsTopY, barW, barsMaxH);
  }

  // Filled segments with gradient
  if (filledSegments > 0) {
    const fillW = filledSegments * (barW + barsGap) - barsGap;
    const fillGrad = ctx.createLinearGradient(0, barsTopY, 0, barsTopY + barsMaxH);
    if (stats.grooveActive) {
      fillGrad.addColorStop(0, "rgba(255,170,248,0.98)");
      fillGrad.addColorStop(1, "rgba(118,72,240,0.92)");
    } else if (stats.grooveReady) {
      fillGrad.addColorStop(0, "rgba(164,255,248,0.98)");
      fillGrad.addColorStop(1, "rgba(78,216,255,0.92)");
    } else {
      fillGrad.addColorStop(0, "rgba(164,186,255,0.96)");
      fillGrad.addColorStop(1, "rgba(86,120,236,0.9)");
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(barsStartX, barsTopY, fillW, barsMaxH);
    ctx.clip();
    for (let i = 0; i < filledSegments; i++) {
      const bx = barsStartX + i * (barW + barsGap);
      ctx.fillStyle = fillGrad;
      ctx.fillRect(bx, barsTopY, barW, barsMaxH);
    }

    // Shimmer sweep
    const shimmerStrength = grooveShimmerStrengthForFill(grooveFill, false);
    const shimmerSpeed = grooveShimmerSpeedForFill(grooveFill, false);
    const shimmerTravel = barsTotalW + fillW + 36;
    const shimmerX = barsStartX - barsTotalW + ((songTimeSec * shimmerSpeed) % shimmerTravel);
    ctx.save();
    ctx.translate(shimmerX, barsTopY);
    ctx.rotate(-Math.PI / 6);
    const shimmerGrad = ctx.createLinearGradient(0, 0, 34, 0);
    shimmerGrad.addColorStop(0, "rgba(255,255,255,0)");
    shimmerGrad.addColorStop(0.48, `rgba(220,248,255,${shimmerStrength.toFixed(3)})`);
    shimmerGrad.addColorStop(0.52, `rgba(255,255,255,${Math.min(0.65, shimmerStrength + 0.2).toFixed(3)})`);
    shimmerGrad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = shimmerGrad;
    ctx.fillRect(-26, -barsMaxH, 88, barsMaxH * 3);
    ctx.restore();
    ctx.restore();

    // Leading edge highlight
    const edgeX = barsStartX + fillW;
    ctx.strokeStyle = stats.grooveReady || stats.grooveActive
      ? "rgba(236,255,255,0.95)"
      : "rgba(196,232,255,0.9)";
    ctx.lineWidth = grooveFill >= 0.95 ? 1.8 : 1.4;
    ctx.beginPath();
    ctx.moveTo(edgeX + 0.5, barsTopY + 1);
    ctx.lineTo(edgeX + 0.5, barsTopY + barsMaxH - 1);
    ctx.stroke();
  }

  ctx.restore();
  ctx.lineWidth = 1;

  // Labels
  ctx.font = `4px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.fillStyle = stats.grooveActive ? "#fff0ff" : stats.grooveReady ? "#e0ffff" : "#ccc";
  ctx.fillText("GROOVE", grooveX, grooveY - 2);
  ctx.textAlign = "right";
  ctx.fillStyle = stats.grooveActive ? "#ffb1ea" : stats.grooveReady ? "#aef9ff" : "#9bb6de";
  const grooveWord = stats.grooveActive ? "ACTIVE" : stats.grooveReady ? "READY" : "BUILD";
  ctx.fillText(grooveWord, grooveX + grooveW, grooveY - 2);

  // Opponent powerups row (mirrors local bottom HUD inventory state)
  const powerups = [
    { key: "HINT", label: "H" },
    { key: "ROWRIP", label: "R" },
    { key: "COLCUT", label: "C" },
    { key: "TRANSMUTE", label: "T" },
    { key: "ZIGZAGBOLT", label: "Z" }
  ] as const;
  const slotW = 60;
  const slotH = 26;
  const slotGap = 4;
  const rowW = powerups.length * slotW + (powerups.length - 1) * slotGap;
  const slotStartX = panelX + Math.floor((VERSUS_PANEL_WIDTH - rowW) / 2);
  const slotY = grooveY + grooveH + 8;
  for (let i = 0; i < powerups.length; i += 1) {
    const entry = powerups[i];
    const count = stats.powerups[entry.key];
    const sx = slotStartX + i * (slotW + slotGap);
    const hasStock = count > 0;
    ctx.fillStyle = hasStock ? "rgba(24,46,78,0.95)" : "rgba(18,22,34,0.95)";
    ctx.fillRect(sx, slotY, slotW, slotH);
    ctx.strokeStyle = hasStock ? "#6ea3ff" : "#3f4d66";
    ctx.strokeRect(sx, slotY, slotW, slotH);
    ctx.textAlign = "left";
    ctx.fillStyle = hasStock ? "#dbeaff" : "#7b8aa4";
    ctx.font = `bold 6px ${GAME_FONT}`;
    ctx.fillText(entry.label, sx + 6, slotY + 10);
    ctx.textAlign = "right";
    ctx.fillStyle = hasStock ? "#ffd700" : "#687a96";
    ctx.fillText(String(count), sx + slotW - 6, slotY + 10);
  }
}

